import { action, computed, observable, makeObservable } from 'mobx';
import queryString from 'query-string';
import { ISODate } from '../../../../../commonv3/types/dates';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../../commonv3/api/RemoteResourceV3';
import { SessionApis } from '../../../../apis/sessions';
import { SessionIndexView, SessionType } from '../../../../types/sessions';
import moment, { Moment } from 'moment-timezone';
import { getLocationFilter, LocationFilter, getLocationSelector } from '../../../molecules/forms/LocationSelector';
import { anyMatch } from '../../../../../commonv3/utils/predicates';
import { SessionTemplateId } from '../../../../../adminv3/types/session';
import { ReviewerIndexView } from '../../../../types/reviewers';
import { CommonsV1ReviewerApis } from '../../../../apis/reviewers';

enum LocationType {
  ONLINE = 'online',
  OFFLINE = 'offline',
}

export enum RoomType {
  ZOOM = 'zoom',
  SHE_STATION = 'she_station',
}

export class SessionListState {
  sessionType: SessionType = SessionType.GROUP_COACHING;
  selectedMultipleDates: ISODate[] = [];
  selectedMonth: Moment | null = null;
  selectedLocationType: LocationType | null = null;
  selectedLocationSelectors: LocationFilter[] = [];
  selectedOnlyOpen = false;
  selectedRoomTypes: RoomType[] = [];
  selectedSessionTemplateId: SessionTemplateId | null = null;
  sessions: RemoteResourceV3<SessionIndexView[]> = RemoteResourceV3.loading();
  groupCoachingCoaches: RemoteResourceV3<ReviewerIndexView[]> = RemoteResourceV3.loading();
  selectedGroupCoachingCoachId = '';
  selectedSessionId: number | null = null;

  constructor() {
    makeObservable(this, {
      sessionType: observable,
      selectedMultipleDates: observable,
      selectedMonth: observable,
      selectedLocationType: observable,
      selectedLocationSelectors: observable,
      selectedOnlyOpen: observable,
      selectedRoomTypes: observable,
      selectedSessionTemplateId: observable,
      sessions: observable,
      groupCoachingCoaches: observable,
      selectedGroupCoachingCoachId: observable,
      selectedSessionId: observable,
      init: action.bound,
      sessionListLoading: computed,
      reloadSessions: action.bound,
      fetchGroupCoachingCoaches: action.bound,
      setSessionType: action.bound,
      setSelectedDate: action.bound,
      setSelectedMonth: action.bound,
      setSelectedOnlyOpen: action.bound,
      setSelectedLocationType: action.bound,
      setSelectedLocationSelectors: action.bound,
      setSelectedRoomTypes: action.bound,
      setSelectedSessionTemplateId: action.bound,
      setSelectedGroupCoachingCoachId: action.bound,
      setSelectedSessionId: action.bound,
      sessionsOfType: computed,
      filteredSessions: computed,
      updateSessionListByQueryString: action.bound,
    });
  }

  async init() {
    await this.reloadSessions();
    await this.fetchGroupCoachingCoaches();
  }

  get sessionListLoading() {
    return this.sessions.loading;
  }

  async reloadSessions() {
    await updateRemoteResourceV3(
      this.sessions,
      (v) => (this.sessions = v),
      () => SessionApis.findAll()
    );
  }

  async fetchGroupCoachingCoaches() {
    await updateRemoteResourceV3(
      this.groupCoachingCoaches,
      (v) => (this.groupCoachingCoaches = v),
      () => CommonsV1ReviewerApis.findAll('group_coaching')
    );
  }

  setSessionType(type: SessionType) {
    this.sessionType = type;
  }

  setSelectedDate(date: ISODate) {
    if (this.selectedMultipleDates.includes(date)) {
      this.selectedMultipleDates = this.selectedMultipleDates.filter((d) => d != date);
    } else {
      this.selectedMultipleDates.push(date);
    }
  }

  setSelectedMonth(month: Moment | null) {
    this.selectedMonth = month;
    if (!moment(this.selectedMultipleDates).startOf('month').isSame(month)) {
      this.selectedMultipleDates = [];
    }
  }

  setSelectedOnlyOpen(selectedOnlyOpen: boolean) {
    this.selectedOnlyOpen = selectedOnlyOpen;
  }

  setSelectedLocationType(locationType: LocationType) {
    this.selectedLocationType = locationType;
  }

  setSelectedLocationSelectors(locationSelectors: LocationFilter[]) {
    this.selectedLocationSelectors = locationSelectors;
  }

  setSelectedRoomTypes(selectedRoomTypes: RoomType[]) {
    this.selectedRoomTypes = selectedRoomTypes;
  }

  setSelectedSessionTemplateId(selectedSessionTemplateId: SessionTemplateId) {
    this.selectedSessionTemplateId = selectedSessionTemplateId;
  }

  setSelectedGroupCoachingCoachId(selectedGroupCoachingCoachId: string) {
    this.selectedGroupCoachingCoachId = selectedGroupCoachingCoachId;
  }

  setSelectedSessionId(selectedSessionId: number | null) {
    this.selectedSessionId = selectedSessionId;
  }

  get sessionsOfType() {
    return this.sessions.getOrElse([]).filter((r) => r.session_type === this.sessionType);
  }

  get filteredSessions() {
    const filteredSessions = this.sessionsOfType.filter((session) => {
      const isSelectedMonth = !this.selectedMonth || moment(session.starts_at).month() === this.selectedMonth.month();
      const isSelectedLocation = anyMatch(this.selectedLocationSelectors.map((s) => getLocationFilter(s)))(session);
      const isSelectedMonthAndLocation = isSelectedMonth && isSelectedLocation;
      if (!isSelectedMonthAndLocation) return false;
      const isSelectedOnlyOpen = !this.selectedOnlyOpen || !(session.active_reservation_count >= session.capacity);
      if (!isSelectedOnlyOpen) return false;
      const filters = this.selectedRoomTypes.map((roomType) => {
        switch (roomType) {
          case RoomType.ZOOM:
            return (s: SessionIndexView) => s.zoom_url;
          case RoomType.SHE_STATION:
            return (s: SessionIndexView) => !s.zoom_url;
        }
      });
      const isSelectedRoomType = filters.length === 0 || filters.some((f) => f(session));
      if (!isSelectedRoomType) return false;
      const isSelectedSessionTemplate =
        !this.selectedSessionTemplateId || session.template_id === this.selectedSessionTemplateId;
      if (!isSelectedSessionTemplate) return false;
      const isSelectedGroupCoachingCoachId =
        this.selectedGroupCoachingCoachId === '' ||
        session.coach_positions.find(
          (coachPosition) => `${coachPosition.user_id}` === this.selectedGroupCoachingCoachId
        );
      if (!isSelectedGroupCoachingCoachId) return false;
      const isSelectedMultipleDates =
        this.selectedMultipleDates.length === 0 ||
        this.selectedMultipleDates.includes(moment(session.starts_at).format('YYYY-MM-DD'));
      if (!isSelectedMultipleDates) return false;
      const isSelectedSessionId = this.selectedSessionId === null || session.id === this.selectedSessionId;
      if (!isSelectedSessionId) return false;
      return true;
    });
    return filteredSessions;
  }

  updateSessionListByQueryString(locationSearch: string): [boolean, boolean, boolean] {
    type QueryParams = {
      [key: string]: string | string[] | undefined;
    };
    const params: QueryParams = queryString.parse(locationSearch);
    const locationIdQuery = params.location_id;
    const onlyOpenQuery = params.only_open;
    const groupCoachingCoachId = params.group_coaching_coach_id;
    const onlyInitialCoachingQuery = params.only_initial_coaching;
    const onlyRegularCoachingQuery = params.only_regular_coaching;
    const onlyRecentlyPublishedQuery = params.only_recently_published;
    const onlyInitialCoaching = onlyInitialCoachingQuery ? onlyInitialCoachingQuery === 'true' : true;
    const onlyRegularCoaching = onlyRegularCoachingQuery ? onlyRegularCoachingQuery === 'true' : true;
    const onlyRecentlyPublished = onlyRecentlyPublishedQuery ? onlyRecentlyPublishedQuery === 'true' : false;
    const date = params.date;
    const sessionId = params.session_id;
    function getNullOrNumberFromQueryString(v: string) {
      return v === 'null' ? null : Number(v);
    }
    if (locationIdQuery) {
      const querySelectedLocationSelectors = Array.isArray(locationIdQuery)
        ? locationIdQuery.map((v) => getLocationSelector(getNullOrNumberFromQueryString(v)))
        : [getLocationSelector(getNullOrNumberFromQueryString(locationIdQuery))];
      this.setSelectedLocationSelectors([...this.selectedLocationSelectors, ...querySelectedLocationSelectors]);
    }
    if (onlyOpenQuery) {
      const querySelectedOnlyOpen = onlyOpenQuery === 'true';
      this.setSelectedOnlyOpen(querySelectedOnlyOpen);
    }
    if (groupCoachingCoachId) {
      this.setSelectedGroupCoachingCoachId(String(groupCoachingCoachId));
    }
    if (date) {
      this.setSelectedMonth(null);
      if (Array.isArray(date)) {
        date.forEach((d) => {
          this.setSelectedDate(moment(d).format('YYYY-MM-DD'));
        });
      } else {
        this.setSelectedDate(moment(date).format('YYYY-MM-DD'));
      }
    }
    if (sessionId) {
      this.setSelectedSessionId(Number(sessionId));
    }
    return [onlyInitialCoaching, onlyRegularCoaching, onlyRecentlyPublished];
  }
}
